import { useCallback, useEffect, useState } from "react";
import { Bell, Inbox } from "lucide-react";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLocation, useNavigate } from "react-router-dom";
import { PushToggle, ensurePush, pushSupported, needsIosInstall } from "@/components/PushToggle";
import { AlertPrompt } from "@/components/AlertPrompt";

export const InboxBell = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ messages: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState(null);
  const loc = useLocation();
  const nav = useNavigate();

  const load = useCallback(() => api.get("/me/inbox").then(({ data: d }) => {
    setData(d);
    const latest = d.messages.find((m) => m.unread);
    const canAsk = (pushSupported() && Notification.permission === "default") || needsIosInstall();
    if (latest && canAsk && localStorage.getItem("ntv_alert_prompted") !== latest.id) {
      localStorage.setItem("ntv_alert_prompted", latest.id);
      setPrompt(latest);
    }
  }).catch(() => {}), []);
  useEffect(() => { load(); ensurePush().catch(() => {}); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, [load]);

  useEffect(() => {
    if (new URLSearchParams(loc.search).get("inbox") === "1") {
      load().then(() => onOpen(true));
      setOpen(true);
      nav(loc.pathname, { replace: true });
    }
  }, [loc.search]); // eslint-disable-line react-hooks/exhaustive-deps

  const onOpen = (o) => {
    setOpen(o);
    if (o) {
      api.post("/me/inbox/read").then(() => setTimeout(() => setData((d) => ({ ...d, unread: 0, messages: d.messages.map((m) => ({ ...m, unread: false })) })), 1500)).catch(() => {});
    }
  };

  return (
    <>
    <AlertPrompt message={prompt} onClose={() => setPrompt(null)} />
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="inbox-bell-btn" aria-label={t("inbox.title")}
          className="relative w-9 h-9 grid place-items-center rounded-full bg-white/10 hover:bg-white/20 transition-colors">
          <Bell size={16} />
          {data.unread > 0 && (
            <span data-testid="inbox-unread-badge" className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-brand text-[10px] font-bold">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" data-testid="inbox-panel" className="w-80 max-w-[calc(100vw-2rem)] p-0 bg-[#121216] border-white/10 text-white">
        <div className="px-4 py-3 border-b border-white/10 font-head font-bold flex items-center gap-2"><Inbox size={16} /> {t("inbox.title")}</div>
        <PushToggle />
        <div className="max-h-96 overflow-y-auto">
          {data.messages.length === 0 ? (
            <p data-testid="inbox-empty" className="px-4 py-8 text-center text-sm text-zinc-500">{t("inbox.empty")}</p>
          ) : data.messages.map((m) => (
            <div key={m.id} data-testid={`inbox-msg-${m.id}`} className={`px-4 py-3 border-b border-white/5 ${m.unread ? "bg-brand/10" : ""}`}>
              <div className="flex items-center gap-2">
                {m.unread && <span className="w-2 h-2 rounded-full bg-brand shrink-0" />}
                <p className="font-semibold text-sm">{m.title}</p>
              </div>
              <p className="text-sm text-zinc-300 mt-1 whitespace-pre-line">{m.body}</p>
              <p className="text-[11px] text-zinc-500 mt-1.5">{new Date(m.created_at).toLocaleString()}</p>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
};
